import React, { useEffect, useRef, useState } from "react";

/**
 * LazyVideo defers loading and playback until the video enters the viewport.
 * It keeps the markup minimal (only renders <video> once visible) and pauses
 * autoplaying videos when they scroll out of view to avoid unnecessary work.
 */
const LazyVideo = ({
  src,
  sources = [],
  poster,
  autoPlay = false,
  muted = true,
  loop = false,
  playsInline = true,
  preload = "metadata",
  className = "",
  videoClassName = "",
  posterClassName = "",
  videoStyle,
  posterStyle,
  style,
  intersectionThreshold = 0.35,
  rootMargin = "200px 0px",
  pauseWhenOutOfView = true,
  onCanPlay,
  onLoadedData,
  onError,
  ...rest
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [hasIntersected, setHasIntersected] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Kick off loading once the container is close to the viewport.
  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined") {
      return undefined;
    }

    if (typeof window.IntersectionObserver === "undefined") {
      setHasIntersected(true);
      setIsVisible(true);
      return undefined;
    }

    let observer;
    try {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setHasIntersected(true);
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        },
        {
          threshold: intersectionThreshold,
          rootMargin,
        }
      );
    } catch (err) {
      setHasIntersected(true);
      setIsVisible(true);
      return undefined;
    }

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [intersectionThreshold, rootMargin]);

  // Auto play/pause when visibility changes.
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return undefined;
    }

    if (autoPlay && isVisible) {
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Ignore play rejections (e.g. browser gesture requirements).
        });
      }
    } else if (pauseWhenOutOfView) {
      videoEl.pause();
    }

    return undefined;
  }, [autoPlay, isVisible, pauseWhenOutOfView]);

  const handleError = (event) => {
    setHasError(true);
    if (onError) {
      onError(event);
    }
  };

  const resolvedSources =
    sources.length > 0
      ? sources
      : src
      ? [{ src, type: rest.type || "video/mp4" }]
      : [];

  const shouldRenderVideo = hasIntersected && !hasError && resolvedSources.length > 0;

  return (
    <div ref={containerRef} className={className} style={style}>
      {shouldRenderVideo ? (
        <video
          ref={videoRef}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          poster={poster}
          preload={isVisible ? preload : "none"}
          className={videoClassName}
          onCanPlay={onCanPlay}
          onLoadedData={onLoadedData}
          onError={handleError}
          style={videoStyle}
          {...rest}
        >
          {resolvedSources.map(({ src: sourceSrc, type }, index) => (
            <source key={`${sourceSrc}-${type || index}`} src={sourceSrc} type={type} />
          ))}
        </video>
      ) : poster ? (
        <img
          src={poster}
          alt=""
          className={posterClassName || videoClassName}
          aria-hidden="true"
          loading="lazy"
          style={posterStyle || videoStyle}
        />
      ) : null}
    </div>
  );
};

export default LazyVideo;
